/**
 * PATH: packages/ui/src/kanban/components/card-detail/DescriptionActionsBar.tsx
 * FILE_ID: UI-KANBAN-CARDDETAIL-DESCACTIONSBAR-001
 * PURPOSE: Inline "tap to apply" actions bar rendered under the card
 *          description textarea. Surfaces the intents detected by
 *          `detectDescriptionActions` (assign @name / set due date /
 *          add an ordered checklist step) as chips so the operator can
 *          sequence work without leaving the description.
 * WHY:     Operator (Bravada, 2026-06-17): "see when I am typing it
 *          should auto fill or create an actions bar.. to help sequence
 *          things properly.." Click-to-apply (not auto-apply) keeps a
 *          false positive (e.g. "complete today" ≠ a real deadline)
 *          cheap — the operator just ignores or dismisses the chip.
 * FLOW:
 *   description text → detectDescriptionActions → chips → click → API
 *                                                                ├ attachAssignee
 *                                                                ├ updateCard(due_date)
 *                                                                └ createChecklistItem
 * DEPENDENCIES:
 *   - ./descriptionActions: pure detector (unit-tested)
 *   - ../../api KanbanApi: attachAssignee / updateCard / createChecklist(+Item)
 *   - @tanstack/react-query: invalidate the card's checklist cache on add
 * RELATED:
 *   - CardDetailDrawer.tsx: mounts this directly below the description
 */

import { useMemo, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { CalendarClock, Check, CheckSquare, ListChecks, Loader2, UserPlus, X } from 'lucide-react'
import type { KanbanApi, KanbanMember } from '../../api'
import type { Card } from '../../types'
import { detectDescriptionActions, type DetectedAction } from './descriptionActions'

export interface DescriptionActionsBarProps {
  /** The live description text the operator is typing. */
  text: string
  card: Card
  api: KanbanApi
  qc: QueryClient
  /** boardId — only used to keep cache invalidation consistent with the
   *  rest of the drawer (the checklist query key is card-scoped). */
  boardId?: string
  /** Org/board member directory for resolving `@name`. */
  members?: KanbanMember[]
  /** Bubble the updated card up so the drawer's local state (assignees /
   *  due date) repaints immediately. */
  onChanged?: (card: Card) => void
  /** Mirror an applied due date into the drawer's date control. */
  onDueApplied?: (iso: string) => void
}

/** An action that the card already satisfies needn't be suggested
 *  (re-opening a card whose "@ron" is already assigned shouldn't nag). */
function isAlreadySatisfied(action: DetectedAction, card: Card): boolean {
  if (action.kind === 'assign') return card.assigned_user_ids?.includes(action.userId) ?? false
  // BUG-2026-08-05-KANBAN-SUGGEST-ACTION-DATE-FIRST — typed dates are
  // next-action dates, not hard due dates.
  if (action.kind === 'due') return (card.action_date ?? null) === action.iso
  // checklist items can't be cheaply diffed here — handled by appliedKeys.
  return false
}

export function DescriptionActionsBar({
  text,
  card,
  api,
  qc,
  members,
  onChanged,
  onDueApplied,
}: DescriptionActionsBarProps) {
  // Keys the operator has already applied OR explicitly dismissed this
  // session — suppressed even though the token still lives in the text.
  const [appliedKeys, setAppliedKeys] = useState<Set<string>>(() => new Set())
  const [busyKey, setBusyKey] = useState<string | null>(null)

  const detected = useMemo(
    () => detectDescriptionActions(text, { members }),
    [text, members],
  )

  const pending = useMemo(
    () => detected.filter((a) => !appliedKeys.has(a.key) && !isAlreadySatisfied(a, card)),
    [detected, appliedKeys, card],
  )

  if (pending.length === 0) return null

  const markDone = (key: string) =>
    setAppliedKeys((prev) => {
      const next = new Set(prev)
      next.add(key)
      return next
    })

  const apply = async (action: DetectedAction) => {
    if (busyKey) return
    setBusyKey(action.key)
    try {
      if (action.kind === 'assign') {
        const updated = await api.attachAssignee(card.id, action.userId)
        onChanged?.(updated)
      } else if (action.kind === 'due') {
        const updated = await api.updateCard(card.id, { action_date: action.iso } as never)
        onChanged?.(updated)
        onDueApplied?.(action.iso)
      } else {
        // Ensure a checklist exists, then append the step. Reuse the
        // first existing checklist so steps accumulate in one list
        // rather than spawning a list per tapped line.
        const lists = await api.listChecklists(card.id)
        const target = lists[0] ?? (await api.createChecklist(card.id, 'Steps'))
        await api.createChecklistItem({ checklist: target.id, title: action.title })
        // Same key ChecklistsSection reads — the new step shows live.
        void qc.invalidateQueries({ queryKey: ['kanban', 'card', card.id, 'checklists'] })
        // Activity reads audit_log_json from the full-card query.
        void qc.invalidateQueries({ queryKey: ['kanban', 'card', card.id] })
      }
      markDone(action.key)
    } catch {
      // The scoped fetch layer dispatches `quotech.kanban.error`, which
      // the host toasts (incl. the 401 re-auth copy). Leave the chip in
      // place (not marked done) so the operator can retry.
    } finally {
      setBusyKey(null)
    }
  }

  const chipFor = (action: DetectedAction) => {
    if (action.kind === 'assign') {
      return { icon: <UserPlus className="h-3 w-3" aria-hidden />, label: `Assign ${action.name}` }
    }
    if (action.kind === 'due') {
      return { icon: <CalendarClock className="h-3 w-3" aria-hidden />, label: `Action ${action.label}` }
    }
    return {
      icon: <CheckSquare className="h-3 w-3" aria-hidden />,
      label: `Step: ${action.title.length > 32 ? `${action.title.slice(0, 32)}…` : action.title}`,
    }
  }

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-card-desc-actions="">
      <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
        <ListChecks className="h-3 w-3" aria-hidden />
        Detected
      </span>
      {pending.map((action) => {
        const { icon, label } = chipFor(action)
        const busy = busyKey === action.key
        return (
          <span
            key={action.key}
            className="group inline-flex items-center overflow-hidden rounded-full border border-blue-200 bg-blue-50 text-[11px] text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300"
          >
            <button
              type="button"
              onClick={() => void apply(action)}
              disabled={!!busyKey}
              title="Tap to apply"
              className="inline-flex items-center gap-1 py-0.5 pl-2 pr-1.5 font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : icon}
              <span className="truncate max-w-[14rem]">{label}</span>
              {!busy && <Check className="h-3 w-3 opacity-50 transition-opacity group-hover:opacity-90" aria-hidden />}
            </button>
            <button
              type="button"
              onClick={() => markDone(action.key)}
              disabled={!!busyKey}
              aria-label={`Dismiss ${label}`}
              title="Dismiss"
              className="border-l border-blue-200 px-1 py-0.5 text-blue-400 hover:bg-blue-100 hover:text-blue-700 dark:border-blue-900/60 dark:hover:bg-blue-900/50"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        )
      })}
    </div>
  )
}
